import { useCallback, useState } from 'react';
import useClientStatusContext from '../context/useClientStatusContext';
import { ClientStatus } from '../../../model/types/ClientStatus';
import { ClientResponse } from '../../../model/types/ClientResponse';
import { ClientStatusError } from '../../../model/types/ClientStatusError';
import { useSecurityContext } from '../../SecurityContext';

interface ReturnType<T> {
  wrapClient(...args): Promise<ClientResponse<T>>;
  data: T;
  status: ClientStatus;
}

type ClientCall<T> = (...args) => Promise<ClientResponse<T>>;

export default <T> (client: ClientCall<T>): ReturnType<T> => {
  const { handleStatus } = useClientStatusContext();
  const { accessToken, organizationId } = useSecurityContext();

  const [status, setStatus] = useState<ClientStatus>({
    failed: false,
    finished: false,
    generalErrors: [],
    fieldErrors: [],
    loading: false,
    responseCode: undefined,
  });
  const [data, setData] = useState<T>(null);

  const wrapClient = useCallback(
    async (...args) => {
      setStatus(prevState => ({ ...prevState, loading: true }));
      try {
        const headers = {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(organizationId && { OrganizationId: encodeURIComponent(organizationId) }),
        };
        const response = await client(...args, { headers });

        const newStatus = {
          responseCode: response.responseCode,
          loading: false,
          finished: true,
          generalErrors: [],
          fieldErrors: [],
          failed: false,
        };

        setData(response.data);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.resolve(response);
      } catch (e) {
        const generalResponseErrors: ClientStatusError[] = [];
        const fieldResponseErrors: ClientStatusError[] = [];

        if (e.response == null) {
          const newStatus = {
            responseCode: 503,
            loading: false,
            finished: true,
            generalErrors: generalResponseErrors,
            fieldErrors: fieldResponseErrors,
            failed: true,
          };

          setData(null);
          setStatus(newStatus);
          handleStatus(newStatus);

          return Promise.reject();
        }

        const statusCode = e.response.status;
        let responseData = e.response.data;
        if (e.response.data instanceof Blob) {
          try {
            responseData = JSON.parse(await e.response.data.text());
            // eslint-disable-next-line no-empty
          } catch (ex) {
          }
        }

        responseData?.generalErrors?.forEach(error => generalResponseErrors.push(
          { message: error },
        ));

        responseData?.fieldErrors?.forEach(error => fieldResponseErrors.push(
          { message: error.message, fieldName: error.field },
        ));

        const newStatus = {
          responseCode: statusCode,
          loading: false,
          finished: true,
          generalErrors: generalResponseErrors,
          fieldErrors: fieldResponseErrors,
          failed: true,
        };

        setData(null);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.reject();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, handleStatus, accessToken, organizationId],
  );

  return {
    wrapClient,
    data,
    status,
  };
};
